/*
 * Role IDs (ETypePartner): 1 = SysAdmin,  2 = Distributor, 3 = Reseller, 4 = TestingCenter,  5 = Author, 6 = Instructor, 7 = Collection, 8 = Proctor
 * Education sectors (ETypeSector): 1 = K12Education,  4 = HigherEducation
 */

const ALL_ROLES = [1, 2, 3, 4, 7, 8];
const SA = [1];
const SA_AND_DIST = [1, 2];
const NO_TC = [1, 2, 3, 7];
const TC = [4];
const NO_SA = [2, 3, 4, 7];
const NO_COLLECTION = [1, 2, 3, 4];

/**
 * Static menu item definition used in the frontend menu registry.
 * Contains rendering data + filtering criteria so the frontend can
 * build and filter the menu tree using AppContext data.
 *
 * Labels live in `useMenuLabels` so FormatJS can extract them.
 */
export type SideNavNavigationItem = {
    id: string;
    children?: SideNavNavigationItem[];
    featureFlag?: string; // Feature flag name — item hidden when flag is disabled.
    icon?: string;
    iconLink?: string;
    labelKey?: string; // Override for useMenuLabels when `id` is not unique.
    linkTarget?: string;
    path?: string;
    roles?: number[]; // Allowed ETypePartner IDs (e.g. [1,2,3,4]). Omit = all roles.
    sectorFilter?: 'education'; // When set, item only appears for education sectors (K12=1, Higher=4).
};

/**
 * Complete menu tree for the SideNav.
 *
 * The backend endpoint GET /Api/Navigation/GetAllowedItems returns a flat list of item IDs the current user is permitted 
 * to see (filtered by permission).
 * 
 * The frontend filters this registry by role, feature flag, and sector using AppContext data, then intersects with the 
 * backend's allowed list.
 *
 * Order in this array = display order in the sidebar.
 */
export const MENU_REGISTRY: SideNavNavigationItem[] = [

    // Home
    {
        id: 'menuHome',
        icon: 'fa-home',
        path: '/',
        roles: ALL_ROLES
    },

    // SKU Management
    {
        id: 'lbSKUManagementSearch',
        icon: 'fa-barcode',
        path: '/sku-management',
        roles: SA
    },

    // Classrooms
    {
        id: 'lbStudGroupSearch',
        icon: 'fa-users',
        path: '/classrooms',
        roles: ALL_ROLES
    },

    // Access Codes
    {
        id: 'lbCodesSearchEdit',
        icon: 'fa-key',
        path: '/access-codes',
        roles: ALL_ROLES
    },

    // Top-level student search — permission-gated only ("Search by name" / SearchByStudent.aspx).
    // The backend (NavigationController.BuildMenu) no longer sector-gates this item.
    {
        id: 'lbStudentsSearch',
        icon: 'fa-graduation-cap',
        path: '/students',
        roles: ALL_ROLES
    },
    {
        id: 'lbMenuLicSearchEdit',
        icon: 'fa-id-card',
        path: '/licenses/search',
        roles: SA
    },
    {
        id: 'lbCustSearch',
        icon: 'fa-square-pen',
        path: '/custom-tests',
        roles: NO_SA
    },
    {
        id: 'lbCollectionEdit',
        icon: 'fa-handshake',
        path: '/associations',
        roles: ALL_ROLES
    },
    {
        id: 'lblFeedbackViews',
        icon: 'fa-commenting',
        path: '/feedback',
        roles: SA
    },

    // Distributors submenu
    {
        id: 'ddlDist',
        children: [
            {
                id: 'lbMenuMpDistiCreate',
                path: '/partners/create?PageState=Create&PType=2'

            },
            {
                id: 'lbMenuMpDistiEdit',
                path: '/partners?PType=2'
            }
        ],
        icon: 'fa-globe',
        roles: SA
    },

    // Resellers submenu
    {
        id: 'ddlResell',
        children: [
            {
                id: 'lbMenuMpResCreate',
                path: '/partners/create?PageState=Create&PType=3'

            },
            {
                id: 'lbMenuMpResEdit',
                path: '/partners?PType=3'
            }
        ],
        icon: 'fa-credit-card',
        roles: SA_AND_DIST
    },

    // Testing Centers submenu
    {
        id: 'ddlTC',
        children: [
            {
                id: 'lbMenuMpTCCreate',
                path: '/partners/create?PageState=Create&PType=4',
                roles: ALL_ROLES

            },
            {
                id: 'lbMenuMpTCEdit',
                path: '/partners?PType=4',
                roles: ALL_ROLES

            },
            {
                id: 'lbMenuMpTCViewExp',
                path: '/reports/11'
            }
        ],
        icon: 'fa-building',
        roles: NO_TC
    },

    // Orders submenu
    {
        id: 'ddlOrd',
        children: [
            {
                id: 'lbOrdCreate',
                path: '/orders/place?OrderFor=Proxy',
                roles: NO_TC
            },
            {
                id: 'lbOrdPending',
                path: '/orders/pending'
            },
            {
                id: 'lbOrdView',
                path: '/orders/history?dir=1&ordType=Processed'
            },
            {
                id: 'lbMyInventory',
                path: '/orders/inventory'
            },
            {
                id: 'lblExtendOrder',
                path: '/orders/extend',
                roles: SA
            },
            {
                id: 'lblFulfillmentManager',
                path: '/orders/manage',
                roles: SA
            }
        ],
        icon: 'fa-clipboard',
        roles: NO_COLLECTION
    },

    {
        id: 'ViewAccounts',
        icon: 'fa-circle-user',
        path: '/administrators',
        roles: ALL_ROLES
    },

    // Reports submenu
    {
        id: 'ddlRep',
        icon: 'fa-folder-open',
        path: '/reports',
        roles: ALL_ROLES
    },

    // Feature Flags submenu
    {
        id: 'lbFeatureFlags',
        children: [
            {
                id: 'lbFeatureFlags',
                labelKey: 'lbFeatureFlagsManage',
                path: '/features/manage'
            },
            {
                id: 'lbFeatureRingManageUser',
                path: '/features/access'
            }
        ],
        icon: 'fa-flag',
        roles: SA
    },

    {
        id: 'bulbSSOMenu',
        featureFlag: 'Admin.Bulb.SSOIntegration',
        iconLink: 'https://content.gmetrix.net/partneruploads/PartnerIcons/bulbwhite.svg',
        linkTarget: 'blank',
        path: '/Admin/Bulb/BulbSSO.aspx',
        roles: TC
    },
    {
        id: 'lbIntegrations',
        icon: 'fa-link',
        path: '/integrations',
        roles: TC
    },
    {
        id: 'lbMenuResources',
        icon: 'fa-paperclip',
        path: '/course-resources',
        roles: TC
    }
];

/**
 * When sector is education AND role is TC (4), these flat items replace the nested `ddlStud` submenu. 
 * They appear at the end of the menu.
 */
export const EDUCATION_TC_FLAT_ITEMS: SideNavNavigationItem[] = [
    {
        id: 'lbStudentMerge',
        icon: 'fa-graduation-cap',
        path: '/students/merge'
    }
];

// Sectors classified as "education" (K12 = 1, HigherEducation = 4). Mirrors the SideNav constant.
const EDUCATION_SECTORS = [1, 4];

/**
 * Item IDs for pages a user reaches by selecting a partner via the
 * partner-lookup flow (see RouteGuard `GUARDS`), but whose selected partner type
 * would not normally pass the page's role/sector filters.
 *
 * Because the user explicitly requested the page — and the backend authorizes
 * them — these items are gated on the acting user's *own* role (see
 * `actingRoleId`) instead of the managed partner's type while managing a proxy
 * partner. Feature flags and the server-side allowedItems list still apply for
 * non-override pages. Every other page keeps the partner's normal role/sector
 * gating, so a managing user still sees what that partner sees.
 */
export const SYSADMIN_PARTNER_OVERRIDE_ITEMS: readonly string[] = [
    'lblExtendOrder', // /orders/extend — SA-only page that requires picking a partner
    'lbOrdCreate' // /orders/place  — requires picking a partner
];

/**
 * Item IDs for pages a partner may reach when placing an order for their *own*
 * inventory — e.g. the "Order More Inventory" CTA on the My Inventory page.
 *
 * The place-order menu item (`lbOrdCreate`, labelled "Order by Proxy") is gated
 * to non-Testing-Center roles for the sidebar entry, because a Testing Center
 * never orders by proxy. A Testing Center can, however, reorder their own
 * inventory, so these pages stay reachable when the selected partner is the
 * acting user's own partner (see `isOwnPartner`). The sidebar entry remains
 * hidden — it is filtered separately and still requires the server allowedItems
 * list — and the backend continues to authorize the actual order.
 */
export const SELF_PARTNER_ORDER_ITEMS: readonly string[] = [
    'lbOrdCreate' // /orders/place — reorder your own inventory
];

export type MenuAccessContext = {

    // The acting user's actual highest role id. For the whitelisted "must pick a
    // partner" override pages, role gating is evaluated against this rather than
    // the managed partner's type — so a proxy-capable user (SysAdmin,
    // Distributor, Reseller) keeps access after selecting a Testing Center to
    // act on behalf of, while an item's own role list still bars anyone else.
    actingRoleId: number;
    allowedItems: string[];
    isFeatureEnabled: (name: string) => boolean;

    // True while a user is acting inside a managed partner context they reached
    // via the partner-lookup flow — i.e. the selected partner is not their own.
    // In that state their effective role (roleId) is the managed partner's type,
    // so the whitelisted override pages fall back to `actingRoleId` instead.
    isManagingProxyPartner: boolean;

    // True when the selected partner is the acting user's own partner (not a
    // proxy/managed partner). Lets a partner reorder their own inventory via the
    // whitelisted SELF_PARTNER_ORDER_ITEMS pages even when the item's own role
    // list would otherwise exclude them (e.g. a Testing Center).
    isOwnPartner: boolean;
    roleId: number;
    sector: number;
};

/**
 * Returns the chain of menu items from the root down to (and including) the item
 * matching `id`, or null when no item with that id exists. Ancestors come first
 * so callers can apply each level's role/feature/sector filters.
 */
const findItemChain = (
    id: string,
    items: SideNavNavigationItem[],
    ancestors: SideNavNavigationItem[] = []
): SideNavNavigationItem[] | null => {

    for (const item of items) {

        const chain = [...ancestors, item];
        if (item.id === id) return chain;

        if (item.children) {

            const childChain = findItemChain(id, item.children, chain);
            if (childChain) return childChain;

        }

    }

    return null;

};

/**
 * True when the current user may load the route mapped to `itemId`.
 *
 * Mirrors the SideNav filtering rules (role, feature flag, sector) across the
 * item and all of its ancestors, then intersects with the server-side
 * permission list (allowedItems). The result is that a route is only reachable
 * when its corresponding menu item would actually be visible to the user.
 */
export const isMenuItemAccessible = (itemId: string, ctx: MenuAccessContext): boolean => {

    const { actingRoleId, allowedItems, isFeatureEnabled, isManagingProxyPartner, isOwnPartner, roleId, sector } = ctx;

    const chain = findItemChain(itemId, MENU_REGISTRY) ?? findItemChain(itemId, EDUCATION_TC_FLAT_ITEMS);
    if (!chain) return false;

    const isEducation = EDUCATION_SECTORS.includes(sector);

    // Only the explicitly whitelisted "must pick a partner" pages let a user who
    // is managing another partner fall back to their own role and skip sector
    // gating. Every other page keeps the managed partner's normal gating.
    const overridePartnerFilters = isManagingProxyPartner && SYSADMIN_PARTNER_OVERRIDE_ITEMS.includes(itemId);

    // A partner reordering their own inventory reaches the place-order page via
    // the "Order More Inventory" CTA. Allow it for their own partner even when the
    // item's own role list (NO_TC) would bar them (e.g. a Testing Center); the
    // backend still authorizes the actual order.
    const allowSelfOrder = isOwnPartner && SELF_PARTNER_ORDER_ITEMS.includes(itemId);

    const relaxPartnerFilters = overridePartnerFilters || allowSelfOrder;

    // For relaxed pages the role gate uses the acting user's real role, so a
    // proxy-capable user retains access while the managed partner (e.g. a Testing
    // Center) would not — the item's own role list still bars anyone not allowed.
    const effectiveRoleId = relaxPartnerFilters ? actingRoleId : roleId;

    // The backend allowedItems list is authoritative for permission-controlled
    // items: it only contains an item that violates its own role list when the
    // user has been explicitly granted the underlying permission (see
    // NavigationController.ComputeAllowedItems). When the target item is present
    // there, honour that grant and skip the target's own role gate — e.g. a
    // Testing Center user who has the "Place an order" permission may reach
    // /orders/place even though lbOrdCreate is otherwise NO_TC.
    const isBackendGranted = allowedItems.includes(itemId);

    const passesFilters = chain.every(item => {

        const isTarget = item.id === itemId;

        // Skip the target item's own role gate when the user may reach it via a
        // self-order flow OR the backend has explicitly granted it (permission
        // active). Ancestor role gates still apply so the page stays within the
        // correct submenu context.
        const skipTargetRoleGate = isTarget && (allowSelfOrder || isBackendGranted);
        if (item.roles && !skipTargetRoleGate && !item.roles.includes(effectiveRoleId)) return false;
        if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;
        if (!relaxPartnerFilters && item.sectorFilter === 'education' && !isEducation) return false;
        return true;

    });

    // The server-side allowedItems list is computed for the *selected* partner, so
    // it won't contain an override page the chosen partner lacks. For the
    // whitelisted pages the user explicitly requested (and the backend
    // authorizes), skip that intersection too — otherwise the override is a no-op.
    return passesFilters && (relaxPartnerFilters || allowedItems.includes(itemId));

};
